/**
 * escpos.ts — Encoder ESC/POS mínimo, autocontenido y puro (sin dependencias de red ni de browser).
 *
 * Portado del spike probado contra hardware real
 * (`plick-print-web-spike/escpos.js`). Suficiente para un ticket de texto:
 * init, alineación, negrita, tamaño, líneas, feed y corte. Produce un
 * `Uint8Array` de bytes crudos listo para escribir por Web Bluetooth.
 *
 * DIFERIDO v1 (NO implementado a propósito): logo/imagen (raster), QR y
 * código de barras. El spike los cargaba vía un encoder externo por CDN, lo
 * cual está prohibido (sin dependencias nuevas). Se dejará como TODO en el
 * builder de ticket cuando se aborden.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

// Alineación horizontal (ESC a n): 0=izquierda, 1=centro, 2=derecha.
export type EscPosAlign = 0 | 1 | 2;

// Modo de tamaño de carácter (GS ! n):
// 0=normal, 1=doble alto, 2=doble ancho, 3=doble alto+ancho.
export type EscPosSize = 0 | 1 | 2 | 3;

const SIZE_MAP: Readonly<Record<EscPosSize, number>> = {
  0: 0x00,
  1: 0x01,
  2: 0x10,
  3: 0x11,
};

// Concatena varios `Uint8Array` en uno solo.
function concat(chunks: readonly Uint8Array[]): Uint8Array {
  let len = 0;
  for (const c of chunks) len += c.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

// Caracteres latinos comunes → byte en CP437 (la página por defecto de la mayoría de
// térmicas; CP437/CP850 comparten estos valores para el español). Permite imprimir ñ y
// acentos correctamente en lugar de '?'.
const CP437_MAP: Record<string, number> = {
  ç: 0x87,
  ü: 0x81,
  é: 0x82,
  â: 0x83,
  ä: 0x84,
  à: 0x85,
  ê: 0x88,
  ë: 0x89,
  è: 0x8a,
  ï: 0x8b,
  î: 0x8c,
  ì: 0x8d,
  Ä: 0x8e,
  É: 0x90,
  ô: 0x93,
  ö: 0x94,
  ò: 0x95,
  û: 0x96,
  ù: 0x97,
  Ö: 0x99,
  Ü: 0x9a,
  á: 0xa0,
  í: 0xa1,
  ó: 0xa2,
  ú: 0xa3,
  ñ: 0xa4,
  Ñ: 0xa5,
  '¿': 0xa8,
  '¡': 0xad,
};

// Codifica texto a bytes CP437. Mapea ñ/acentos a su byte CP437; para otros no-ASCII
// intenta quitar el diacrítico (p.ej. Á→A) y, si no es representable, usa '?' (0x3f).
function encodeText(str: string): Uint8Array {
  const out: number[] = [];
  for (const ch of str) {
    const mapped = CP437_MAP[ch];
    if (mapped !== undefined) {
      out.push(mapped);
      continue;
    }
    const code = ch.codePointAt(0) ?? 0x3f;
    if (code < 0x80) {
      out.push(code);
      continue;
    }
    const stripped = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const s = stripped.charCodeAt(0);
    out.push(stripped.length === 1 && s < 0x80 ? s : 0x3f);
  }
  return new Uint8Array(out);
}

// Builder fluido de comandos ESC/POS. El constructor emite `ESC @` (init/reset)
// de forma que el primer byte de la salida siempre reinicia la impresora.
export class EscPos {
  private readonly chunks: Uint8Array[] = [];

  constructor() {
    // ESC @ -> inicializar / reset.
    // ESC t 0 -> seleccionar página de código CP437, para que la impresora
    // interprete los bytes de ñ/acentos (0x80-0xAF) correctamente.
    this.chunks.push(new Uint8Array([ESC, 0x40, ESC, 0x74, 0x00]));
  }

  // Inserta bytes crudos sin interpretación.
  raw(bytes: Uint8Array | readonly number[]): this {
    this.chunks.push(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
    return this;
  }

  // Alineación: 0=izquierda, 1=centro, 2=derecha (ESC a n).
  align(n: EscPosAlign): this {
    return this.raw([ESC, 0x61, n]);
  }

  // Negrita on/off (ESC E n).
  bold(on: boolean): this {
    return this.raw([ESC, 0x45, on ? 1 : 0]);
  }

  // Tamaño de carácter (GS ! n). Ver EscPosSize.
  size(mode: EscPosSize): this {
    return this.raw([GS, 0x21, SIZE_MAP[mode]]);
  }

  // Escribe una línea de texto seguida de salto de línea.
  line(str = ''): this {
    this.raw(encodeText(str));
    this.raw([LF]);
    return this;
  }

  // Escribe texto sin salto de línea final.
  text(str: string): this {
    return this.raw(encodeText(str));
  }

  // Avanza `n` líneas en blanco.
  feed(n = 1): this {
    return this.raw(new Uint8Array(Math.max(0, n)).fill(LF));
  }

  // Corte total del papel (GS V 0). Muchas térmicas de bolsillo lo ignoran
  // silenciosamente sin error.
  cut(): this {
    return this.raw([GS, 0x56, 0x00]);
  }

  // Materializa todos los comandos acumulados a un único `Uint8Array`.
  encode(): Uint8Array {
    return concat(this.chunks);
  }
}
